/**
 * AtlasGuildModule — GUILDS (V, menu bar → Guilds). The P14 SWG loop (a guild hall deed → place it → charter at the
 * hall) and, since 2026-09-24, the guild's COURT and LAND:
 *   GUILD      your guild: the Guild Master and the council, the message of the day, the TREASURY (give to it), the
 *              CLAIM (tier, radius, the weekly upkeep and how it went), the door (open / by application), leave. Not in
 *              a guild: the hall you stand at (join / apply / charter it), and the way to found one.
 *   COURT      the elections — Guild Master by one vote, the council by APPROVAL (tick up to as many names as seats),
 *              each on a term the server opens itself — the MOTIONS the court decides (spend · expand · policy · expel
 *              · recall), and the applications waiting.
 *   PROPERTY   the buildings the guild raised on its claim; "Raise guild property" hands one of your deeds to the
 *              placement map in guild mode (the same rules as every deed, plus: inside the claim, the claim's tier).
 *   MEMBERS    the roster (the court may remove members), the versona workforce and the storehouse.
 *   DIRECTORY  every guild in the Commons — player guilds (join / apply) and the VERSONA trade guilds (their master,
 *              council and members), each with "Show on map".
 * Every action is a reducer; the window never re-renders under a focused field (the typed-text law).
 */
import type { App, IModule } from 'enginex';
import { Identity } from 'spacetimedb';
import { atlas } from '../state.js';
import { makeWindow, type UiWindow } from '../lib/ui_window.js';
import { nameFromTables } from '../lib/versona_names.js';
import { bus } from '../bus.js';
import { el, btn, input, select, tabs, head, chip, muted, field, card, act, ms } from './town/town_ui.js';
import * as G from '../lib/guild_rules.js';
import { STRUCTURE_LABELS, footprintOf } from '@shared/types/structures.js';

interface Ts { microsSinceUnixEpoch: bigint }
interface IdLike { isEqual(o: unknown): boolean; toHexString(): string }
interface GuildRow { guildId: bigint; hallStructureId: bigint; name: string; tag: string; founder: IdLike; leaderId: IdLike; motd: string; createdAt: Ts }
interface MemberRow { guildMemberId: bigint; guildId: bigint; identity: IdLike; title: string; joinedAt: Ts }
interface ElectionRow { guildElectionId: bigint; guildId: bigint; office: string; opensAt: Ts; closesAt: Ts; resolved: boolean; winner: IdLike }
interface CandidateRow { guildCandidateId: bigint; guildElectionId: bigint; identity: IdLike; pitch: string; at: Ts }
interface VoteRow { guildElectionId: bigint; voter: IdLike; candidate: IdLike }
interface StructureRow { structureId: bigint; owner: IdLike; structureType: string; posX: number; posZ: number; worldId: bigint; maintPool: bigint; yaw: number }
interface AccountRow { identity: IdLike; username: string }
interface GuildVersonaRow { gvId: bigint; guildId: bigint; versonaId: bigint; job: string; recruitedAt: Ts }
interface GuildStoreRow { gsId: bigint; guildId: bigint; key: string; qty: number }
interface CourtRow { guildId: bigint; masterTermEnds: Ts; councilTermEnds: Ts; seats: number; policy: string }
interface TerrRow { territoryKey: string; kind: string; guildId: bigint; worldId: bigint; x: number; z: number; tier: number; radius: number; upkeepDueAt: Ts; upkeepNote: string }
interface MotionRow { motionId: bigint; guildId: bigint; kind: string; target: IdLike; amount: bigint; text: string; proposer: IdLike; opensAt: Ts; closesAt: Ts; status: string; yes: number; no: number; outcome: string }
interface MotionVoteRow { motionId: bigint; voter: IdLike; yes: boolean }
interface GuildStructRow { structureId: bigint; guildId: bigint; placedBy: IdLike; structureType: string; at: Ts }
interface AppRow { appKey: string; guildId: bigint; applicant: IdLike; note: string; at: Ts }
interface VGuildRow { vguildId: bigint; settlementId: bigint; trade: string; seat: number; name: string; tag: string; worldId: bigint; x: number; z: number; tier: number; masterVersona: bigint; termEnds: Ts; foundedAt: Ts }
interface VMemberRow { vgmKey: string; vguildId: bigint; versonaId: bigint; rank: string; joinedAt: Ts }
interface DeedRow { deedId: bigint; owner: IdLike; structureType: string; packed: boolean }
interface WalletRow { identity: IdLike; sov: bigint }

type R = Record<string, (a: unknown) => Promise<void>>;

/** The nine guild posts (server GUILD_JOBS mirror). */
const GUILD_JOBS = [
  'guild_guard', 'farmer', 'miner', 'crafter', 'droid_builder',
  'weapon_builder', 'greeter', 'guild_manager', 'guild_diplomat',
] as const;
const JOB_HINT: Record<string, string> = {
  guild_guard: 'patrols the hall — an unguarded storehouse loses stock',
  farmer: 'works the plot → grain + produce',
  miner: 'works the cut → ferrite ore, stone, the odd crystal',
  crafter: 'refines ore/fiber → metal ingots + fiber weave',
  droid_builder: 'ingots + crystal → circuit lattices → an Ember work droid',
  weapon_builder: 'ingots + timber → blaster barrels + weapon grips',
  greeter: 'welcomes visitors at the hall door',
  guild_manager: 'runs the floor — production ×1.5 per manager (cap ×2)',
  guild_diplomat: 'walks embassies — every member\'s town standing rises',
};
const HALL_RANGE_M = 60;
const MOTION_HINT: Record<string, string> = {
  spend: 'pay a member from the treasury',
  expand: 'raise the claim one tier (the treasury pays)',
  policy: 'open the door to all, or admit by application',
  expel: 'remove a member (not the Guild Master)',
  recall: 'call an early election for Guild Master',
};
const TABS = [
  { key: 'guild', label: 'Guild' }, { key: 'court', label: 'Court' }, { key: 'property', label: 'Property' },
  { key: 'members', label: 'Members' }, { key: 'directory', label: 'Directory' },
];

const until = (t: Ts): string => {
  const d = ms(t) - Date.now();
  if (d <= 0) return 'now';
  const h = d / 3_600_000;
  return h >= 48 ? `${Math.round(h / 24)} d` : h >= 1 ? `${Math.round(h)} h` : `${Math.max(1, Math.round(d / 60_000))} min`;
};

export class AtlasGuildModule implements IModule {
  readonly name = 'AtlasGuild';
  private win: UiWindow | null = null;
  private body: HTMLDivElement | null = null;
  private timer: number | null = null;
  private subbed = false;
  private rosterSubbed = false;
  private walletSubbed = new Set<string>();
  private tab = 'guild';
  private dirSel: bigint | null = null;
  private f = { name: '', tag: '', motd: '', donate: '500', mkind: 'spend', mtarget: '', mamount: '0', mtext: '', pitch: new Map<string, string>(), note: '' };
  private greetAt = 0;
  private greetTimer: number | null = null;
  private onKeyV: ((e: KeyboardEvent) => void) | null = null;

  init(_app: App): void {
    void _app;
    // The greeter earns their post: approaching your hall while one stands
    // duty plays a personal welcome (3-min cooldown; cheap 4 s poll).
    this.greetTimer = window.setInterval(() => {
      try {
        const conn = atlas.conn;
        if (!conn?.identity || atlas.currentWorldId === null || atlas.spaceFlight) return;
        if (Date.now() - this.greetAt < 180_000) return;
        const me = conn.identity as unknown as IdLike;
        const mine = this.myMember(me);
        if (!mine) return;
        const g = this.guildOf(mine.guildId);
        const hall = g ? this.structure(g.hallStructureId) : undefined;
        if (!g || !hall || hall.worldId !== atlas.currentWorldId) return;
        // The greeter stands at the door; measure from the hall's walls (the hall is 95 × 87 m).
        const hfp = footprintOf(hall.structureType);
        if (G.distanceToBox(atlas.localX, atlas.localZ, { x: hall.posX, z: hall.posZ, hx: hfp.hx, hz: hfp.hz, yaw: hall.yaw }) > 20) return;
        const greeter = this.tbl<GuildVersonaRow>('guildVersona').find((v) => v.guildId === g.guildId && v.job === 'greeter');
        if (!greeter) return;
        this.greetAt = Date.now();
        bus.emit('atlas:status', { text: `🤝 ${nameFromTables(conn, greeter.versonaId)}: “Welcome back to ${g.name}, honored member.”` });
      } catch { /* tables mid-churn */ }
    }, 4000);
    this.onKeyV = (e: KeyboardEvent): void => {
      if (e.code !== 'KeyV' || e.ctrlKey || e.metaKey || e.altKey) return;
      const t = e.target as HTMLElement | null;
      if (t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA')) return;
      if (atlas.loadingGate || atlas.currentWorldId === null || atlas.spaceFlight) return;
      this.toggle();
    };
    window.addEventListener('keydown', this.onKeyV);
    (window as unknown as { __guilds?: unknown }).__guilds = {
      open: (tab?: string) => { if (tab) this.tab = tab; if (!this.win?.isOpen()) this.toggle(); else this.render(); },
      close: () => this.win?.setOpen(false),
      tab: () => this.tab,
      select: (vguildId: number) => { this.dirSel = BigInt(vguildId); this.tab = 'directory'; this.ensureRosters(); this.render(); },
      mine: () => { const me = atlas.conn?.identity as unknown as IdLike | undefined; const m = me ? this.myMember(me) : null; return m ? { guildId: Number(m.guildId), title: m.title } : null; },
    };
  }

  dispose(): void {
    if (this.greetTimer !== null) { window.clearInterval(this.greetTimer); this.greetTimer = null; }
    if (this.onKeyV) { window.removeEventListener('keydown', this.onKeyV); this.onKeyV = null; }
    if (this.timer !== null) { window.clearInterval(this.timer); this.timer = null; }
  }

  // ─── Rows ──────────────────────────────────────────────────────────────────────
  private tbl<T>(name: string): T[] {
    try {
      const t = (atlas.conn?.db as unknown as Record<string, { iter(): Iterable<T> } | undefined>)[name];
      return t ? [...t.iter()] : [];
    } catch { return []; }
  }
  private find<T>(table: string, index: string, key: unknown): T | undefined {
    try { return (atlas.conn?.db as unknown as Record<string, Record<string, { find(k: unknown): T | undefined }>>)[table]?.[index]?.find(key); } catch { return undefined; }
  }
  private guildOf(id: bigint): GuildRow | undefined { return this.find<GuildRow>('guild', 'guildId', id); }
  private structure(id: bigint): StructureRow | undefined { return this.find<StructureRow>('structure', 'structureId', id); }
  private myMember(me: IdLike): MemberRow | null {
    try {
      const idx = (atlas.conn?.db as unknown as { guildMember?: { guildMemberByIdentity: { filter(i: unknown): Iterable<MemberRow> } } }).guildMember;
      if (idx) for (const m of idx.guildMemberByIdentity.filter(me)) return m;
    } catch { /* pre-publish */ }
    return null;
  }
  private roster(guildId: bigint): MemberRow[] { return this.tbl<MemberRow>('guildMember').filter((m) => m.guildId === guildId); }
  private reducers(): R | null { return (atlas.conn?.reducers ?? null) as unknown as R | null; }
  private nameOf(id: IdLike): string {
    if (/^(0x)?0+$/.test(id.toHexString())) return 'vacant'; // a Guild Master's seat left empty (the next election fills it)
    const a = this.find<AccountRow>('account', 'identity', id);
    return a ? a.username : `${id.toHexString().slice(0, 8)}…`;
  }
  private vaultHex(guildId: bigint): string { return new Identity(G.GUILD_VAULT_BASE + guildId).toHexString().replace(/^0x/, ''); }
  private treasury(guildId: bigint): bigint | null {
    const hex = this.vaultHex(guildId);
    this.ensureWallet(hex);
    const w = this.tbl<WalletRow>('wallet').find((r) => r.identity.toHexString().replace(/^0x/, '') === hex);
    return w ? w.sov : null;
  }

  private ensureSubs(): void {
    if (this.subbed || !atlas.conn) return;
    this.subbed = true;
    for (const q of ['SELECT * FROM guild', 'SELECT * FROM guild_member', 'SELECT * FROM guild_election',
      'SELECT * FROM guild_candidate', 'SELECT * FROM guild_vote', 'SELECT * FROM account',
      'SELECT * FROM guild_versona', 'SELECT * FROM guild_store', 'SELECT * FROM guild_court', 'SELECT * FROM guild_territory',
      'SELECT * FROM guild_motion', 'SELECT * FROM guild_motion_vote', 'SELECT * FROM guild_structure', 'SELECT * FROM guild_application',
      'SELECT * FROM versona_guild']) {
      try { atlas.conn.subscriptionBuilder().subscribe([q]); } catch { /* pre-publish */ }
    }
  }
  /** The versona rosters (≈ 3k rows) only once the directory is looked at. */
  private ensureRosters(): void {
    if (this.rosterSubbed || !atlas.conn) return;
    this.rosterSubbed = true;
    try { atlas.conn.subscriptionBuilder().subscribe(['SELECT * FROM versona_guild_member']); } catch { /* pre-publish */ }
  }
  private ensureWallet(hex: string): void {
    if (this.walletSubbed.has(hex) || !atlas.conn) return;
    this.walletSubbed.add(hex);
    try { atlas.conn.subscriptionBuilder().subscribe([`SELECT * FROM wallet WHERE identity = 0x${hex}`]); } catch { /* pre-publish */ }
  }

  // ─── The window ────────────────────────────────────────────────────────────────
  private toggle(): void {
    if (this.win?.isOpen()) { this.win.setOpen(false); return; }
    this.ensureSubs();
    if (!this.win) {
      this.win = makeWindow({ id: 'guilds', title: 'Guilds', icon: 'orbit', width: 560, height: 620, minWidth: 440, minHeight: 380, x: 140, y: 80 });
      this.body = el('div', 'display:flex;flex-direction:column;gap:10px;padding:10px 12px');
      this.win.body.appendChild(this.body);
      this.win.body.addEventListener('focusout', () => window.setTimeout(() => this.softRender(), 0));
    }
    this.win.setOpen(true);
    if (this.timer === null) {
      this.timer = window.setInterval(() => {
        if (this.win?.isOpen()) this.softRender();
        else if (this.timer !== null) { window.clearInterval(this.timer); this.timer = null; }
      }, 2000);
    }
    this.render();
  }
  /** Never re-render under a focused field (a half-typed pitch would be wiped). */
  private softRender(): void {
    const a = document.activeElement as HTMLElement | null;
    if (a && this.win?.body.contains(a) && (a.tagName === 'INPUT' || a.tagName === 'SELECT' || a.tagName === 'TEXTAREA')) return;
    this.render();
  }

  private render(): void {
    const b = this.body;
    const conn = atlas.conn;
    if (!b || !conn?.identity || !this.win?.isOpen()) return;
    const scroll = this.win.body.scrollTop;
    b.replaceChildren();
    const me = conn.identity as unknown as IdLike;
    const mine = this.myMember(me);
    const g = mine ? this.guildOf(mine.guildId) ?? null : null;
    b.appendChild(tabs(TABS, this.tab, (k) => { this.tab = k; if (k === 'directory') this.ensureRosters(); this.render(); }));
    const pane = el('div', 'display:flex;flex-direction:column;gap:10px');
    pane.dataset.guildTab = this.tab;
    if (this.tab === 'directory') this.paneDirectory(pane, me, g);
    else if (!g || !mine) this.paneNoGuild(pane, me);
    else if (this.tab === 'guild') this.paneGuild(pane, g, mine);
    else if (this.tab === 'court') this.paneCourt(pane, g, mine, me);
    else if (this.tab === 'property') this.paneProperty(pane, g, mine, me);
    else this.paneMembers(pane, g, mine, me);
    b.appendChild(pane);
    this.win.body.scrollTop = scroll;
  }

  // ─── Not in a guild ────────────────────────────────────────────────────────────
  private nearbyHall(): StructureRow | null {
    let best: StructureRow | null = null;
    let bestD = HALL_RANGE_M;
    for (const s of this.tbl<StructureRow>('structure')) {
      if (s.structureType !== 'guild_hall') continue;
      if (atlas.currentWorldId !== null && s.worldId !== atlas.currentWorldId) continue;
      const d = Math.hypot(s.posX - atlas.localX, s.posZ - atlas.localZ);
      if (d < bestD) { bestD = d; best = s; }
    }
    return best;
  }
  private paneNoGuild(p: HTMLElement, me: IdLike): void {
    const r = this.reducers();
    const hall = this.nearbyHall();
    p.appendChild(muted('You belong to no guild. Join one at its hall (or apply, where the door opens by application), or found your own.'));
    if (hall) {
      const g = this.tbl<GuildRow>('guild').find((x) => x.hallStructureId === hall.structureId);
      const c = card();
      if (g) {
        const court = this.find<CourtRow>('guildCourt', 'guildId', g.guildId);
        c.appendChild(el('div', 'font:600 14px var(--ax-font);color:var(--ax-hi)', `${g.name} <${g.tag}>`));
        c.appendChild(muted(`Guild Master ${this.nameOf(g.leaderId)} · ${this.roster(g.guildId).length} members · the door ${court?.policy === 'apply' ? 'opens by application' : 'is open'}`));
        if (court?.policy === 'apply') {
          const note = input('A line for the court', this.f.note, 'text', '240px');
          note.addEventListener('input', () => { this.f.note = note.value; });
          c.appendChild(field('Apply', note, btn('Apply', () => { void act(r?.applyToGuild({ guildId: g.guildId, note: this.f.note }), 'Application sent — the court will answer'); }, true)));
        } else {
          c.appendChild(btn(`Join ${g.name}`, () => { void act(r?.joinGuild({ guildId: g.guildId }), `Welcome to ${g.name}`, () => this.render()); }, true));
        }
      } else if (hall.owner.isEqual(me)) {
        c.appendChild(el('div', 'font:600 14px var(--ax-font);color:var(--ax-hi)', 'Charter your guild'));
        c.appendChild(muted(`Your hall stands unchartered. A charter claims ${G.playerGuildRadius(1)} m around it — free land, with no one else's building on it.`));
        const nm = input('Guild name', this.f.name, 'text', '200px'); nm.maxLength = 24;
        nm.addEventListener('input', () => { this.f.name = nm.value; });
        const tg = input('TAG', this.f.tag, 'text', '70px'); tg.maxLength = 5;
        tg.addEventListener('input', () => { this.f.tag = tg.value; });
        c.appendChild(field('Name', nm, tg, btn('Charter', () => {
          void act(r?.createGuild({ structureId: hall.structureId, name: this.f.name.trim(), tag: this.f.tag.trim() }), `Chartered ${this.f.name.trim()}`, () => { this.f.name = ''; this.f.tag = ''; this.render(); });
        }, true)));
      } else {
        c.appendChild(muted('This hall is unchartered — only its owner may raise a guild here.'));
      }
      p.appendChild(c);
    }
    p.appendChild(head('Found a guild'));
    p.appendChild(muted(`1. Craft a guild hall deed (5 lots) · 2. Place it with the placement map — the hall needs ${G.playerGuildRadius(1)} m of open country around it · 3. Stand at your hall and charter.`));
    // The deed is CRAFTED (2026-09-25): this button called `grantDeed` — a free deed faucet, now debug-gated on the
    // server (a player got "debug" refused). It opens the workbench on the guild hall deed's schematic instead.
    p.appendChild(btn('Craft a guild hall deed', () => { bus.emit('atlas:open-workbench', { tab: 'craft', key: 'deed_guild_hall' }); }));
    p.appendChild(muted('The Directory tab lists every guild, player and versona.'));
  }

  // ─── Your guild ────────────────────────────────────────────────────────────────
  private paneGuild(p: HTMLElement, g: GuildRow, mine: MemberRow): void {
    const r = this.reducers();
    const court = this.find<CourtRow>('guildCourt', 'guildId', g.guildId);
    const terr = this.find<TerrRow>('guildTerritory', 'territoryKey', `p:${g.guildId}`);
    const roster = this.roster(g.guildId);
    const isCourt = mine.title === G.MASTER_TITLE || G.isCouncilTitle(mine.title);
    const c = card();
    c.appendChild(el('div', 'font:600 16px var(--ax-font);color:var(--ax-hi)', `${g.name} <${g.tag}>`));
    const chips = el('div', 'display:flex;gap:6px;flex-wrap:wrap');
    chips.appendChild(chip(`Guild Master: ${this.nameOf(g.leaderId)}`, 'info'));
    chips.appendChild(chip(`${roster.length} members`));
    chips.appendChild(chip(mine.title === G.MASTER_TITLE ? 'you are the Guild Master' : G.isCouncilTitle(mine.title) ? 'you sit on the council' : 'you are a member', isCourt ? 'good' : ''));
    chips.appendChild(chip(court?.policy === 'apply' ? 'door: by application' : 'door: open'));
    c.appendChild(chips);
    const council = roster.filter((m) => G.isCouncilTitle(m.title));
    c.appendChild(muted(`Council (${council.length}/${court?.seats ?? G.councilSeats(roster.length)} seats): ${council.length ? council.map((m) => this.nameOf(m.identity)).join(', ') : 'no one seated yet'}`));
    if (court) c.appendChild(muted(`The Guild Master's term ends in ${until(court.masterTermEnds)} · the council's in ${until(court.councilTermEnds)}`));
    if (g.motd) c.appendChild(el('div', 'padding:6px 8px;border:1px solid var(--ax-line);border-radius:var(--ax-radius);color:var(--ax-text)', g.motd));
    p.appendChild(c);
    if (isCourt) {
      const m = input('Message of the day', this.f.motd, 'text', '300px'); m.maxLength = 140;
      m.addEventListener('input', () => { this.f.motd = m.value; });
      p.appendChild(field('Message', m, btn('Set', () => { void act(r?.setGuildMotd({ guildId: g.guildId, motd: this.f.motd.trim() }), 'Message set'); })));
    }
    // The treasury.
    p.appendChild(head('Treasury'));
    const t = this.treasury(g.guildId);
    const tc = card();
    tc.appendChild(el('div', 'font:600 15px var(--ax-mono);color:var(--ax-text)', t === null ? '…' : `${t.toLocaleString()} ⓒ`));
    tc.appendChild(muted('Every member may give. The court spends it by motion; it pays the claim\'s upkeep and keeps guild property standing.'));
    const d = input('Amount', this.f.donate, 'number', '100px');
    d.addEventListener('input', () => { this.f.donate = d.value; });
    tc.appendChild(field('Give', d, btn('Give to the treasury', () => {
      const n = BigInt(Math.max(0, Math.floor(Number(this.f.donate) || 0)));
      void act(r?.donateToGuild({ guildId: g.guildId, amount: n }), `Gave ${n} ⓒ to ${g.name}`);
    })));
    p.appendChild(tc);
    // The claim.
    p.appendChild(head('Territory'));
    const cc = card();
    if (!terr) cc.appendChild(muted('The guild holds no claim (the hall is gone, or its land was taken before claims existed).'));
    else {
      cc.appendChild(el('div', 'color:var(--ax-text)', `Tier ${terr.tier} of ${G.GUILD_MAX_TIER} · ${terr.radius} m around the hall`));
      cc.appendChild(muted(`Upkeep ${G.GUILD_UPKEEP_PER_TIER * terr.tier} ⓒ a week, due in ${until(terr.upkeepDueAt)}${terr.upkeepNote ? ` — last: ${terr.upkeepNote}` : ''}. Unpaid, the claim shrinks one tier.`));
      if (terr.tier < G.GUILD_MAX_TIER) {
        const next = terr.tier + 1;
        cc.appendChild(muted(`Tier ${next}: ${G.GUILD_TERRITORY_RADIUS[next]} m for ${G.GUILD_TIER_COST[next].toLocaleString()} ⓒ, needs ${G.GUILD_TIER_MIN_MEMBERS[next]} members (players and the versonas you employ). The court decides — a motion to expand.`));
      }
      cc.appendChild(muted('Inside the claim only members build; the court raises guild property there (the Property tab).'));
      cc.appendChild(btn('Show on the world map', () => { bus.emit('atlas:map-focus', { x: terr.x, z: terr.z, zoom: 1.2, guilds: true }); }));
    }
    p.appendChild(cc);
    p.appendChild(btn(`Leave ${g.name}`, () => { void act(r?.leaveGuild({}), 'You left the guild', () => this.render()); }));
  }

  // ─── The court ─────────────────────────────────────────────────────────────────
  private paneCourt(p: HTMLElement, g: GuildRow, mine: MemberRow, me: IdLike): void {
    const r = this.reducers();
    const court = this.find<CourtRow>('guildCourt', 'guildId', g.guildId);
    const roster = this.roster(g.guildId);
    const isCourt = mine.title === G.MASTER_TITLE || G.isCouncilTitle(mine.title);
    const seats = court?.seats ?? G.councilSeats(roster.length);
    const elections = this.tbl<ElectionRow>('guildElection').filter((e) => e.guildId === g.guildId);
    const open = elections.filter((e) => !e.resolved);
    p.appendChild(head('Elections'));
    p.appendChild(muted(`The Guild Master is chosen by one vote each; the council's ${seats} seats by approval — tick up to ${seats} names, the most approved sit. Each term runs ${Math.round(G.GUILD_TERM_S / 86_400)} days; the polls open themselves when a term ends or a seat stands empty, and stay open ${Math.round(G.GUILD_ELECTION_S / 3_600)} h.`));
    for (const e of open) {
      const cs = this.tbl<CandidateRow>('guildCandidate').filter((c) => c.guildElectionId === e.guildElectionId);
      const vs = this.tbl<VoteRow>('guildVote').filter((v) => v.guildElectionId === e.guildElectionId);
      const closed = ms(e.closesAt) <= Date.now();
      const c = card();
      c.dataset.guildElection = e.office;
      c.appendChild(el('div', 'font:600 14px var(--ax-font);color:var(--ax-hi)', e.office === 'leader' ? 'Guild Master' : `Council — ${seats} seats`));
      c.appendChild(muted(`${cs.length} standing · ${new Set(vs.map((v) => v.voter.toHexString())).size} voted · ${closed ? 'the polls have closed' : `closes in ${until(e.closesAt)}`}`));
      const myVotes = vs.filter((v) => v.voter.isEqual(me));
      for (const cand of cs) {
        const row = el('div', 'display:flex;gap:8px;align-items:center');
        const n = vs.filter((v) => v.candidate.isEqual(cand.identity)).length;
        row.appendChild(el('span', 'flex:1;color:var(--ax-text)', `${this.nameOf(cand.identity)}${cand.pitch ? ` — “${cand.pitch}”` : ''}`));
        row.appendChild(chip(`${n}`));
        const chosen = myVotes.some((v) => v.candidate.isEqual(cand.identity));
        if (!closed) {
          if (e.office === 'council') {
            const b = btn(chosen ? '✓ approved' : 'Approve', () => {
              void act(chosen ? r?.withdrawGuildApproval({ guildElectionId: e.guildElectionId, candidate: cand.identity }) : r?.castGuildVote({ guildElectionId: e.guildElectionId, candidate: cand.identity }), chosen ? 'Approval withdrawn' : 'Approved', () => this.render());
            }, chosen);
            b.dataset.guildApprove = cand.identity.toHexString();
            row.appendChild(b);
          } else {
            const b = btn(chosen ? '✓ your vote' : 'Vote', () => { void act(r?.castGuildVote({ guildElectionId: e.guildElectionId, candidate: cand.identity }), 'Ballot cast', () => this.render()); }, chosen);
            b.dataset.guildVote = cand.identity.toHexString();
            row.appendChild(b);
          }
        }
        c.appendChild(row);
      }
      if (!closed && !cs.some((c2) => c2.identity.isEqual(me)) && !(e.office === 'council' && mine.title === G.MASTER_TITLE)) {
        const key = String(e.guildElectionId);
        const pi = input('Your pitch', this.f.pitch.get(key) ?? '', 'text', '240px'); pi.maxLength = 100;
        pi.addEventListener('input', () => { this.f.pitch.set(key, pi.value); });
        c.appendChild(field('Stand', pi, btn('Stand for it', () => { void act(r?.runForGuildOffice({ guildElectionId: e.guildElectionId, pitch: (this.f.pitch.get(key) ?? '').trim() }), 'You stand for office', () => this.render()); })));
      }
      if (closed) c.appendChild(btn('Count the votes', () => { void act(r?.tallyGuildElection({ guildElectionId: e.guildElectionId }), 'The votes are counted', () => this.render()); }, true));
      p.appendChild(c);
    }
    const canOpen = (['leader', 'council'] as const).filter((o) => !open.some((e) => e.office === o));
    if (canOpen.length) {
      const row = el('div', 'display:flex;gap:6px;flex-wrap:wrap;align-items:center');
      row.appendChild(el('span', 'color:var(--ax-muted);font-size:12px', 'Call an election:'));
      for (const o of canOpen) row.appendChild(btn(o === 'leader' ? 'Guild Master' : 'Council', () => { void act(r?.openGuildElection({ guildId: g.guildId, office: o }), `${o === 'leader' ? 'Guild Master' : 'Council'} election opened — ${Math.round(G.GUILD_ELECTION_S / 3_600)} h of polling`, () => this.render()); }));
      p.appendChild(row);
      p.appendChild(muted('A term that still runs cannot be cut short by a member: the Guild Master may call an early council election, and the council recalls a master by motion.'));
    }
    for (const e of elections.filter((x) => x.resolved).slice(-3)) {
      p.appendChild(muted(`past · ${e.office === 'leader' ? 'Guild Master' : 'council'} → ${e.winner.toHexString().replace(/^0x/, '').replace(/0/g, '') ? this.nameOf(e.winner) : 'no one (the office stood as it was)'}`));
    }
    // Motions.
    p.appendChild(head('Motions'));
    p.appendChild(muted(`The court (Guild Master + council) decides by majority — a motion passes once more than half the court says yes, and is enacted at once; it lapses after ${Math.round(G.GUILD_MOTION_S / 3_600)} h.`));
    const motions = this.tbl<MotionRow>('guildMotion').filter((m) => m.guildId === g.guildId).sort((a, b) => Number(b.motionId - a.motionId));
    const mv = this.tbl<MotionVoteRow>('guildMotionVote');
    for (const m of motions.slice(0, 12)) {
      const c = card();
      c.dataset.guildMotion = String(m.motionId);
      const who = m.kind === 'spend' || m.kind === 'expel' ? ` ${this.nameOf(m.target)}` : '';
      const amt = m.kind === 'spend' ? ` ${m.amount.toLocaleString()} ⓒ` : m.kind === 'policy' ? (m.amount === 1n ? ' — by application' : ' — an open door') : '';
      c.appendChild(el('div', 'color:var(--ax-text)', `${m.kind}${amt}${who}${m.text ? ` — ${m.text}` : ''}`));
      c.appendChild(muted(`laid by ${this.nameOf(m.proposer)} · ${m.yes} yes · ${m.no} no · ${m.status === 'open' ? `closes in ${until(m.closesAt)}` : m.status}${m.outcome ? ` — ${m.outcome}` : ''}`));
      if (m.status === 'open' && isCourt) {
        const my = mv.find((v) => v.motionId === m.motionId && v.voter.isEqual(me));
        const row = el('div', 'display:flex;gap:6px');
        row.appendChild(btn(my?.yes ? '✓ yes' : 'Yes', () => { void act(r?.voteGuildMotion({ motionId: m.motionId, yes: true }), 'Voted yes', () => this.render()); }, !!my?.yes));
        row.appendChild(btn(my && !my.yes ? '✓ no' : 'No', () => { void act(r?.voteGuildMotion({ motionId: m.motionId, yes: false }), 'Voted no', () => this.render()); }, !!my && !my.yes));
        c.appendChild(row);
      }
      p.appendChild(c);
    }
    if (isCourt) {
      const c = card();
      c.appendChild(el('div', 'font:600 13px var(--ax-font);color:var(--ax-hi)', 'Lay a motion'));
      const kind = select(G.MOTION_KINDS.map((k) => ({ value: k, label: `${k} — ${MOTION_HINT[k]}` })), this.f.mkind);
      kind.addEventListener('change', () => { this.f.mkind = kind.value; kind.blur(); this.render(); });
      c.appendChild(field('Kind', kind));
      if (this.f.mkind === 'spend' || this.f.mkind === 'expel') {
        const members = roster.filter((m) => this.f.mkind === 'spend' || m.title !== G.MASTER_TITLE);
        const tsel = select(members.map((m) => ({ value: m.identity.toHexString(), label: this.nameOf(m.identity) })), this.f.mtarget || members[0]?.identity.toHexString() || '');
        this.f.mtarget = tsel.value;
        tsel.addEventListener('change', () => { this.f.mtarget = tsel.value; });
        c.appendChild(field('Member', tsel));
      }
      if (this.f.mkind === 'spend') {
        const a = input('ⓒ', this.f.mamount, 'number', '110px');
        a.addEventListener('input', () => { this.f.mamount = a.value; });
        c.appendChild(field('Amount', a));
      }
      if (this.f.mkind === 'policy') {
        const ps = select([{ value: '0', label: 'an open door' }, { value: '1', label: 'by application' }], this.f.mamount === '1' ? '1' : '0');
        this.f.mamount = ps.value;
        ps.addEventListener('change', () => { this.f.mamount = ps.value; });
        c.appendChild(field('Door', ps));
      }
      const tx = input('Why (optional)', this.f.mtext, 'text', '280px'); tx.maxLength = 160;
      tx.addEventListener('input', () => { this.f.mtext = tx.value; });
      c.appendChild(field('Note', tx));
      c.appendChild(btn('Lay it before the court', () => {
        const target = roster.find((m) => m.identity.toHexString() === this.f.mtarget)?.identity ?? atlas.conn!.identity;
        const amount = BigInt(Math.max(0, Math.floor(Number(this.f.mamount) || 0)));
        void act(r?.proposeGuildMotion({ guildId: g.guildId, kind: this.f.mkind, target, amount, text: this.f.mtext.trim() }), 'The motion is laid (it carries your yes)', () => { this.f.mtext = ''; this.render(); });
      }, true));
      p.appendChild(c);
      const apps = this.tbl<AppRow>('guildApplication').filter((a2) => a2.guildId === g.guildId);
      if (apps.length) {
        p.appendChild(head(`Applications (${apps.length})`));
        for (const a2 of apps) {
          const row = el('div', 'display:flex;gap:6px;align-items:center');
          row.appendChild(el('span', 'flex:1;color:var(--ax-text)', `${this.nameOf(a2.applicant)}${a2.note ? ` — “${a2.note}”` : ''}`));
          row.appendChild(btn('Admit', () => { void act(r?.answerGuildApplication({ guildId: g.guildId, applicant: a2.applicant, accept: true }), 'Admitted', () => this.render()); }, true));
          row.appendChild(btn('Decline', () => { void act(r?.answerGuildApplication({ guildId: g.guildId, applicant: a2.applicant, accept: false }), 'Declined', () => this.render()); }));
          p.appendChild(row);
        }
      }
    }
  }

  // ─── Guild property ────────────────────────────────────────────────────────────
  private paneProperty(p: HTMLElement, g: GuildRow, mine: MemberRow, me: IdLike): void {
    const r = this.reducers();
    const terr = this.find<TerrRow>('guildTerritory', 'territoryKey', `p:${g.guildId}`);
    const isCourt = mine.title === G.MASTER_TITLE || G.isCouncilTitle(mine.title);
    const rows = this.tbl<GuildStructRow>('guildStructure').filter((s) => s.guildId === g.guildId);
    p.appendChild(head(`Guild property (${rows.length})`));
    if (!rows.length) p.appendChild(muted('Nothing raised on the claim yet.'));
    for (const gs of rows) {
      const s = this.structure(gs.structureId);
      const row = el('div', 'display:flex;gap:8px;align-items:center');
      row.dataset.guildProperty = String(gs.structureId);
      row.appendChild(el('span', 'flex:1;color:var(--ax-text)', `${STRUCTURE_LABELS[gs.structureType] ?? gs.structureType} — raised by ${this.nameOf(gs.placedBy)}`));
      if (s) row.appendChild(chip(`pool ${s.maintPool.toLocaleString()} ⓒ`));
      if (isCourt) row.appendChild(btn('Take down', () => { void act(r?.releaseGuildStructure({ structureId: gs.structureId }), 'Taken down — its deed went back to whoever raised it', () => this.render()); }));
      p.appendChild(row);
    }
    p.appendChild(head('Raise guild property'));
    if (!isCourt) { p.appendChild(muted('The Guild Master and the council raise buildings on the claim.')); return; }
    if (!terr) { p.appendChild(muted('The guild holds no claim.')); return; }
    p.appendChild(muted(`From one of your own deeds: the whole building inside the claim (${terr.radius} m), the same ground rules as any deed, and the type allowed at tier ${terr.tier}. It takes no lots of yours; the treasury keeps it (two days of upkeep at a time).`));
    const deeds = this.tbl<DeedRow>('structureDeed').filter((d) => d.owner.isEqual(me) && d.packed);
    const usable = deeds.filter((d) => G.isGuildStructureType(d.structureType));
    if (!usable.length) p.appendChild(muted(`You carry no deed a guild raises (${Object.keys(G.GUILD_STRUCTURE_MIN_TIER).map((k) => STRUCTURE_LABELS[k] ?? k).slice(0, 8).join(', ')} …). Craft or claim one first.`));
    for (const d of usable) {
      const need = G.GUILD_STRUCTURE_MIN_TIER[d.structureType];
      const row = el('div', 'display:flex;gap:8px;align-items:center');
      row.appendChild(el('span', 'flex:1;color:var(--ax-text)', `${STRUCTURE_LABELS[d.structureType] ?? d.structureType}${need > terr.tier ? ` — needs a tier-${need} claim` : ''}`));
      const b = btn('Place on the claim', () => {
        this.win?.setOpen(false);
        bus.emit('atlas:structure-place', { deedId: String(d.deedId), structureType: d.structureType, guildId: String(g.guildId) });
      }, true);
      b.disabled = need > terr.tier;
      b.dataset.guildPlace = String(d.deedId);
      row.appendChild(b);
      p.appendChild(row);
    }
  }

  // ─── Members, the workforce, the storehouse ────────────────────────────────────
  private paneMembers(p: HTMLElement, g: GuildRow, mine: MemberRow, me: IdLike): void {
    const r = this.reducers();
    const roster = this.roster(g.guildId);
    const isCourt = mine.title === G.MASTER_TITLE || G.isCouncilTitle(mine.title);
    p.appendChild(head(`Members (${roster.length})`));
    for (const m of roster.sort((a, b) => (a.title === G.MASTER_TITLE ? -2 : G.isCouncilTitle(a.title) ? -1 : 0) - (b.title === G.MASTER_TITLE ? -2 : G.isCouncilTitle(b.title) ? -1 : 0))) {
      const row = el('div', 'display:flex;gap:8px;align-items:center');
      row.appendChild(el('span', 'flex:1;color:var(--ax-text)', this.nameOf(m.identity) + (m.identity.isEqual(me) ? ' (you)' : '')));
      if (m.title === G.MASTER_TITLE) row.appendChild(chip('Guild Master', 'info'));
      else if (G.isCouncilTitle(m.title)) row.appendChild(chip('council', 'info'));
      const canKick = isCourt && !m.identity.isEqual(me) && m.title !== G.MASTER_TITLE && (mine.title === G.MASTER_TITLE || !G.isCouncilTitle(m.title));
      if (canKick) row.appendChild(btn('Remove', () => { void act(r?.kickGuildMember({ guildId: g.guildId, target: m.identity }), 'Member removed', () => this.render()); }));
      p.appendChild(row);
    }
    const staff = this.tbl<GuildVersonaRow>('guildVersona').filter((v) => v.guildId === g.guildId);
    p.appendChild(head(`Versona workforce (${staff.length})`));
    if (!staff.length) p.appendChild(muted('No versonas serve yet. Befriend a townsperson (talks, gifts, favours) until they regard you as a FRIEND, then Recruit them from their talk pane (E). A versona sworn to a town\'s trade guild stays with it.'));
    else if (!staff.some((v) => v.job === 'guild_guard')) p.appendChild(el('div', 'color:var(--ax-warn);font-size:12px', 'No guard on duty — the storehouse loses stock every shift.'));
    for (const v of staff) {
      const row = el('div', 'display:flex;gap:8px;align-items:center');
      row.appendChild(el('span', 'flex:1;color:var(--ax-text)', nameFromTables(atlas.conn, v.versonaId)));
      const sel = select([{ value: '', label: '— unassigned —' }, ...GUILD_JOBS.map((j) => ({ value: j, label: j.replace(/_/g, ' ') }))], v.job);
      sel.title = JOB_HINT[v.job] ?? '';
      sel.addEventListener('change', () => { void act(r?.assignGuildJob({ versonaId: v.versonaId, job: sel.value }), sel.value === '' ? 'Post cleared' : `Assigned: ${sel.value.replace(/_/g, ' ')}`); sel.blur(); });
      row.appendChild(sel);
      row.appendChild(btn('Release', () => { void act(r?.dismissGuildVersona({ versonaId: v.versonaId }), 'Released from service — they walk home'); }));
      p.appendChild(row);
    }
    const store = this.tbl<GuildStoreRow>('guildStore').filter((s) => s.guildId === g.guildId && s.qty > 0).sort((a, b) => (a.key < b.key ? -1 : 1));
    p.appendChild(head(`Storehouse (${store.length} kinds)`));
    if (!store.length) p.appendChild(muted('Empty shelves — assign farmers and miners, and the chain fills them.'));
    for (const s of store) {
      const row = el('div', 'display:flex;gap:8px;align-items:center');
      row.appendChild(el('span', 'flex:1;color:var(--ax-text)', s.key.replace(/^resource:/, '').replace(/^item:/, '⚙ ').replace(/_/g, ' ')));
      row.appendChild(chip(`×${s.qty}`));
      row.appendChild(btn('Collect 10', () => { void act(r?.collectGuildStore({ key: s.key, qty: 10 }), 'Drawn into your inventory'); }));
      p.appendChild(row);
    }
  }

  // ─── Every guild in the Commons ────────────────────────────────────────────────
  private paneDirectory(p: HTMLElement, me: IdLike, myGuild: GuildRow | null): void {
    const r = this.reducers();
    const conn = atlas.conn;
    const guilds = this.tbl<GuildRow>('guild');
    const terrs = new Map(this.tbl<TerrRow>('guildTerritory').map((t) => [t.territoryKey, t]));
    const dist = (x: number, z: number): string => { const d = Math.hypot(x - atlas.localX, z - atlas.localZ); return d > 9_999 ? `${(d / 1000).toFixed(0)} km` : `${Math.round(d)} m`; };
    p.appendChild(head(`Player guilds (${guilds.length})`));
    if (!guilds.length) p.appendChild(muted('No player guilds yet — the first charter awaits.'));
    for (const g of guilds) {
      const t = terrs.get(`p:${g.guildId}`);
      const court = this.find<CourtRow>('guildCourt', 'guildId', g.guildId);
      const c = card();
      c.dataset.guildDir = `p:${g.guildId}`;
      c.appendChild(el('div', 'font:600 13px var(--ax-font);color:var(--ax-hi)', `${g.name} <${g.tag}>${myGuild?.guildId === g.guildId ? ' — yours' : ''}`));
      c.appendChild(muted(`Guild Master ${this.nameOf(g.leaderId)} · ${this.roster(g.guildId).length} members${t ? ` · claim tier ${t.tier} (${t.radius} m) · ${dist(t.x, t.z)} away` : ''} · ${court?.policy === 'apply' ? 'by application' : 'open door'}`));
      const row = el('div', 'display:flex;gap:6px');
      if (t) row.appendChild(btn('Show on map', () => { bus.emit('atlas:map-focus', { x: t.x, z: t.z, zoom: 1.2, guilds: true }); }));
      if (!myGuild && court?.policy === 'apply') row.appendChild(btn('Apply', () => { void act(r?.applyToGuild({ guildId: g.guildId, note: '' }), 'Application sent'); }));
      c.appendChild(row);
      p.appendChild(c);
    }
    const vgs = this.tbl<VGuildRow>('versonaGuild').sort((a, b) => Math.hypot(a.x - atlas.localX, a.z - atlas.localZ) - Math.hypot(b.x - atlas.localX, b.z - atlas.localZ));
    const vms = this.tbl<VMemberRow>('versonaGuildMember');
    const byGuild = new Map<string, VMemberRow[]>();
    for (const m of vms) { const k = String(m.vguildId); let l = byGuild.get(k); if (!l) { l = []; byGuild.set(k, l); } l.push(m); }
    p.appendChild(head(`Trade guilds of the towns (${vgs.length})`));
    p.appendChild(muted('When ten townsfolk share a trade the town charters a guild at a seat outside its walls; its members elect a Master and a council every term, pay a coin of dues on market days, feed their hungry, and practise their craft at the hall.'));
    if (!vgs.length) p.appendChild(muted('No town has chartered a trade guild yet.'));
    for (const v of vgs.slice(0, 60)) {
      const ms2 = byGuild.get(String(v.vguildId)) ?? [];
      const c = card();
      c.dataset.guildDir = `v:${v.vguildId}`;
      c.appendChild(el('div', 'font:600 13px var(--ax-font);color:var(--ax-hi)', `${v.name} <${v.tag}>`));
      c.appendChild(muted(`${v.trade.replace(/_/g, ' ')} · Master ${v.masterVersona ? nameFromTables(conn, v.masterVersona) : '—'} · ${ms2.length || '…'} members · claim tier ${v.tier} (${terrs.get(`v:${v.vguildId}`)?.radius ?? G.versonaGuildRadius(v.tier)} m) · ${dist(v.x, v.z)} away`));
      const row = el('div', 'display:flex;gap:6px');
      row.appendChild(btn('Show on map', () => { bus.emit('atlas:map-focus', { x: v.x, z: v.z, zoom: 1.2, guilds: true }); }));
      row.appendChild(btn(this.dirSel === v.vguildId ? 'Hide the court' : 'The court', () => { this.dirSel = this.dirSel === v.vguildId ? null : v.vguildId; this.ensureRosters(); this.render(); }));
      c.appendChild(row);
      if (this.dirSel === v.vguildId) {
        const master = ms2.find((m) => m.rank === 'master');
        const council = ms2.filter((m) => m.rank === 'council');
        c.appendChild(muted(`Master: ${master ? nameFromTables(conn, master.versonaId) : '—'} · Council: ${council.map((m) => nameFromTables(conn, m.versonaId)).join(', ') || '—'}`));
        c.appendChild(muted(`Members: ${ms2.filter((m) => m.rank === 'member').slice(0, 24).map((m) => nameFromTables(conn, m.versonaId)).join(', ')}${ms2.length > 26 ? ' …' : ''}`));
        c.appendChild(muted(`Term ends in ${until(v.termEnds)} · founded ${new Date(ms(v.foundedAt)).toLocaleDateString()}`));
      }
      p.appendChild(c);
    }
    void me;
  }
}
